import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';

const PW_RULES = [
  [v => v.length >= 12,        'At least 12 characters'],
  [v => /[A-Z]/.test(v),       'One uppercase letter'],
  [v => /[a-z]/.test(v),       'One lowercase letter'],
  [v => /[0-9]/.test(v),       'One number'],
  [v => /[^A-Za-z0-9]/.test(v),'One special character'],
];

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [form, setForm]     = useState({ password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone]     = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => { if (!token) navigate('/login', { replace: true }); }, [token, navigate]);

  const passChecks = PW_RULES.map(([fn, label]) => ({ label, ok: fn(form.password) }));
  const passValid  = passChecks.every(c => c.ok);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!passValid) { setError('Password does not meet all requirements.'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    setError(''); setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password: form.password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch(err) {
      setError(err?.error || 'Reset failed. The link may have expired. Please request a new one.');
    } finally { setLoading(false); }
  }

  if (done) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg)'}}>
      <div style={{textAlign:'center',maxWidth:'380px',padding:'20px'}}>
        <div style={{fontSize:'48px',marginBottom:'16px'}}>✅</div>
        <h3 style={{fontSize:'20px',fontWeight:'800',color:'var(--text)',marginBottom:'8px'}}>Password reset successfully</h3>
        <p style={{fontSize:'13px',color:'var(--muted)'}}>Redirecting to login…</p>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg)',fontFamily:'inherit'}}>
      <div style={{width:'100%',maxWidth:'420px',padding:'0 20px'}}>
        <div style={{textAlign:'center',marginBottom:'28px'}}>
          <div style={{width:56,height:56,borderRadius:'14px',background:'linear-gradient(135deg,#5B5BD6,#7C3AED)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'24px',fontWeight:'900',color:'#fff',margin:'0 auto 16px'}}>T</div>
          <h2 style={{fontSize:'24px',fontWeight:'800',color:'var(--text)',margin:'0 0 6px'}}>Set new password</h2>
          <p style={{color:'var(--muted)',fontSize:'14px',margin:0}}>Choose a strong password for your account</p>
        </div>

        {error && (
          <div style={{background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.25)',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',color:'#DC2626',display:'flex',gap:'8px',alignItems:'center'}}>
            <span>⚠️</span><span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{marginBottom:'14px'}}>
            <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'var(--text)',marginBottom:'6px'}}>New Password</label>
            <div style={{position:'relative'}}>
              <input className="input" type={showPass?'text':'password'} value={form.password}
                onChange={e=>{setForm(f=>({...f,password:e.target.value}));setError('');}}
                placeholder="Enter new password" autoFocus
                style={{width:'100%',padding:'11px 44px 11px 14px',fontSize:'14px',borderRadius:'10px',boxSizing:'border-box'}}/>
              <button type="button" onClick={()=>setShowPass(v=>!v)} tabIndex={-1}
                style={{position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--muted)',fontSize:'16px',padding:'2px',lineHeight:1}}>
                {showPass?'🙈':'👁️'}
              </button>
            </div>
          </div>

          {/* Password requirements */}
          {form.password && (
            <div style={{marginBottom:'14px',padding:'10px 14px',background:'var(--surface2)',borderRadius:'8px',border:'1px solid var(--border)'}}>
              {passChecks.map(({label,ok}) => (
                <div key={label} style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',color:ok?'#16A34A':'var(--muted)',marginBottom:'3px'}}>
                  <span>{ok?'✓':'○'}</span><span>{label}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{marginBottom:'20px'}}>
            <label style={{display:'block',fontSize:'13px',fontWeight:'600',color:'var(--text)',marginBottom:'6px'}}>Confirm Password</label>
            <input className="input" type={showPass?'text':'password'} value={form.confirm}
              onChange={e=>{setForm(f=>({...f,confirm:e.target.value}));setError('');}}
              placeholder="Confirm new password"
              style={{width:'100%',padding:'11px 14px',fontSize:'14px',borderRadius:'10px',boxSizing:'border-box',
                borderColor: form.confirm && form.confirm!==form.password ? '#DC2626' : undefined}}/>
            {form.confirm && form.confirm !== form.password && (
              <div style={{fontSize:'11px',color:'#DC2626',marginTop:'4px'}}>Passwords do not match</div>
            )}
          </div>

          <button type="submit" disabled={loading || !passValid || form.password !== form.confirm}
            style={{width:'100%',padding:'12px',fontSize:'14px',fontWeight:'700',borderRadius:'10px',border:'none',
              cursor:(loading||!passValid||form.password!==form.confirm)?'not-allowed':'pointer',
              background:(loading||!passValid||form.password!==form.confirm)?'#9CA3AF':'linear-gradient(135deg,#5B5BD6,#7C3AED)',
              color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'}}>
            {loading?(<><div style={{width:14,height:14,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>Saving…</>):'Set New Password'}
          </button>
          <div style={{textAlign:'center',marginTop:'16px'}}>
            <Link to="/login" style={{color:'var(--muted)',fontSize:'13px',textDecoration:'none'}}>← Back to login</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
